import { useCallback, useEffect, useRef } from "react";

const MOBILE = "(max-width: 639px)";
const SKIP = "input, textarea, select, [role=slider], [contenteditable=true], [data-no-swipe]";

const blocked = (target, root, axis) => {
  if (target.closest?.(SKIP)) return true;
  for (let n = target; n && n !== root.parentElement; n = n.parentElement) {
    if (axis === "y" && n.scrollTop > 0) return true;
    if (axis === "x" && n.scrollWidth > n.clientWidth + 1 && getComputedStyle(n).overflowX !== "hidden" && getComputedStyle(n).overflowX !== "visible") return true;
  }
  return false;
};

export const useSwipeClose = (axis, closeRef, forwardedRef) => {
  const elRef = useRef(null);
  const setRef = useCallback((node) => {
    elRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  useEffect(() => {
    const el = elRef.current;
    if (!el || !axis) return;
    let s = null;
    const tr = (d) => (axis === "y" ? `translate3d(0,${d}px,0)` : `translate3d(${d}px,0,0)`);
    const reset = () => { el.style.transition = "transform 220ms cubic-bezier(.2,.8,.2,1)"; el.style.transform = ""; };
    const start = (e) => {
      if (!window.matchMedia(MOBILE).matches || e.touches.length !== 1 || blocked(e.target, el, axis)) return (s = null);
      const t = e.touches[0];
      s = { x: t.clientX, y: t.clientY, t: Date.now(), d: 0, on: false, off: false };
    };
    const move = (e) => {
      if (!s || s.off) return;
      const t = e.touches[0];
      const dx = t.clientX - s.x, dy = t.clientY - s.y;
      const main = axis === "y" ? dy : dx, cross = axis === "y" ? dx : dy;
      if (!s.on) {
        if (Math.abs(main) < 8 && Math.abs(cross) < 8) return;
        if (main <= 0 || Math.abs(cross) > Math.abs(main)) { s.off = true; return; }
        s.on = true;
        el.style.transition = "none";
      }
      s.d = Math.max(0, main);
      el.style.transform = tr(s.d);
      if (e.cancelable) e.preventDefault();
    };
    const end = () => {
      if (!s?.on) return (s = null);
      const v = s.d / Math.max(1, Date.now() - s.t);
      if (s.d > 120 || (v > 0.5 && s.d > 30)) {
        el.style.transition = "transform 180ms ease-out";
        el.style.transform = tr(axis === "y" ? el.offsetHeight + 40 : el.offsetWidth + 40);
        setTimeout(() => closeRef.current?.click(), 160);
      } else reset();
      s = null;
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end);
    el.addEventListener("touchcancel", end);
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
    };
  });

  return setRef;
};
